import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { toast } from "sonner";
import * as Icons from "lucide-react";

interface Props {
    value?: string;
    open?: boolean;
    onOpenChange?: (open:boolean) => void;
    onValueChange?: (value:string | undefined) => void;
    types?: ("data" | "screening" | "scouting" | "check" | "other")[];
    disabled?: boolean;
    trigger?: React.ReactNode;
    loadAllIfNotypesFound?: boolean;
}

const SelectReportTemplate = ({ value, open: openProp, onOpenChange, onValueChange, types, disabled, trigger, loadAllIfNotypesFound }: Props) => {
    const { trpc } = useTRPC();

    const [isOpen, setIsOpen] = React.useState<boolean>(openProp ?? false);

    useEffect(() => {
        setIsOpen(openProp ?? false);
    }, [openProp]);

    const handleOpenChange = (next: boolean) => {
        setIsOpen(next);
        onOpenChange?.(next);
    };

    const [search, setSearch] = React.useState('');
    const [selectedValue, setSelectedValue] = React.useState<string | undefined>(value);

    const { data: templates } = useQuery({
        queryKey: ["report-templates", types ?? []],
        queryFn: async () => {
            const templates = await trpc.reports.templates.search.query({ type: types, loadAllIfNotypesFound: loadAllIfNotypesFound });
            return templates;
        },
        onError: (err) => {
            if (err instanceof Error) {
                toast.error(err.message);
            } else if (err instanceof TRPCClientError) {
                toast.error(err.message);
            } else {
                toast.error("Er is een onbekende fout opgetreden.");
            }
        }
    });

    useEffect(() => {
        if (value !== selectedValue) setSelectedValue(value);
    }, [value]); 

    const handleSelectUser = (user_id: string) => {
        if (selectedValue === user_id) {
            setSelectedValue(undefined);
            onValueChange?.(undefined);
        } else {
            setSelectedValue(user_id);
            onValueChange?.(user_id);
        }

        handleOpenChange(false);
    };

    const filterReports = (data: NonNullable<typeof templates>['reports']) => {
        if (!search) return data;
        return data.filter((user) =>
            user?.name?.toLowerCase().includes(search.toLowerCase())
        );
    };

    const selected = templates?.reports?.find(u => u.id === selectedValue);

    return (
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild disabled={disabled}>
                {trigger || (
                    <Button variant='outline' className="w-full" disabled={disabled}>
                        {selected
                            ? `${selected.name}`
                            : 'Selecteer rapport sjabloon...'
                        }
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="p-0 rounded-3xl gap-0 overflow-hidden" hideCloseButton>
                <div className="flex items-center relative">
                    <input
                        value={search}
                        onChange={(e) => { setSearch(e.currentTarget.value); }}
                        placeholder="Zoek naar sjabloon"
                        className="focus:outline-none py-3 px-4 bg-input/50 rounded-3xl text-sm w-full h-12"
                    />
                    <button onClick={() => { handleOpenChange(false); }} className="absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground size-12 aspect-square hover:bg-muted rounded-full flex items-center justify-center">
                        <Icons.X className="w-4 h-4 text-muted-foreground" />
                    </button>
                </div>

                {(templates && templates?.reports?.length > 0) &&
                    <div className="max-h-96 overflow-auto flex flex-col gap-1 p-2">
                        {filterReports(templates?.reports)?.map((report) => (
                            <button
                                onClick={() => { handleSelectUser(report.id); }}
                                className="flex items-center justify-between gap-4 p-2 rounded-xl hover:bg-muted text-left"
                                key={report.id}
                            >
                                <div className="flex items-center gap-2">
                                    <div>
                                        <p className="font-medium">{report?.name}</p>
                                        <p className="text-xs text-muted-foreground">{report?.type?.join(' - ')}</p>
                                    </div>
                                </div>

                                {report.id === selectedValue && <Icons.Check className="w-4 h-4 text-foreground" />}
                            </button>
                        ))}
                    </div>
                }
            </DialogContent>
        </Dialog>
    );
};

export default SelectReportTemplate;